// Which island a stage gets (B3, B4 second half; owner's decision 2026-10-14). In Japan the built,
// unnamed places stand in for anywhere without an island of its own: Big City, Castle Town and
// Mountain Village are the generic Japanese city, town and village. Outside Japan a neutral generic
// island is coming (candidates in docs/renders/targets/run-2026-10-14; not built until the owner
// picks), so stages there use a clearly marked placeholder isle, spot `generic`.
import { z } from 'zod'

export const JAPAN_GENERIC = { city: 'bigcity', town: 'castletown', village: 'village' } as const
/** The placeholder island for a stage outside Japan (until the generic island is picked). */
export const GENERIC_SPOT = 'generic'

/**
 * B5: the generic world island's archetypes (docs/places/generic-world.md) and the place ids they get
 * once built (`src/map/places/<id>/`, listed in the registry).
 */
export const WORLD_GENERIC = { town: 'world-town', coast: 'world-coast', mountain: 'world-mountain' } as const
export type WorldArchetype = keyof typeof WORLD_GENERIC

/**
 * Which archetypes are built. Empty until the owner picks a still and the island is made; then the
 * swap is one line here (e.g. `{ town: true }`) and every `generic` stage stands on it: on the Map
 * (map/bindings mapView) and on the Trip tab (stage/trip/isles makeIsle). Stages keep `spot: 'generic'`,
 * so nothing stored changes.
 */
export const WORLD_GENERIC_BUILT: Readonly<Partial<Record<WorldArchetype, true>>> = {}

/** The place a `generic` stage stands on: the first built world island (town, coast, mountain), else `fallback`. */
export function genericStandIn(fallback: string, built: Readonly<Partial<Record<WorldArchetype, true>>> = WORLD_GENERIC_BUILT): string {
  const a = (Object.keys(WORLD_GENERIC) as WorldArchetype[]).find((k) => built[k])
  return a ? WORLD_GENERIC[a] : fallback
}

/** A place name looked up through the worker (GeoNames' free search), coordinates rounded. */
export const GeoPlace = z.object({
  name: z.string(),
  /** ISO 3166-1 alpha-2, e.g. JP, PT. */
  countryCode: z.string().length(2),
  country: z.string(),
  admin1: z.string().optional(),
  at: z.tuple([z.number(), z.number()]),
  timezone: z.string().optional(),
  population: z.number().optional(),
})
export type GeoPlace = z.infer<typeof GeoPlace>

interface GeoNamesHit {
  name?: string
  countryCode?: string
  countryName?: string
  adminName1?: string
  lat?: string | number
  lng?: string | number
  population?: number
  /** Feature class: P = populated place, A = admin area, others = hills, lakes, spots… */
  fcl?: string
  timezone?: { timeZoneId?: string }
}

/**
 * GeoNames' searchJSON answer → our places (about 1 km precision; nothing else kept). Towns and
 * admin areas come first, other features after them, each in GeoNames' order. An error answer
 * (`{ status: … }`, e.g. a bad username) is no places.
 */
export function readGeoNames(json: unknown): GeoPlace[] {
  const hits = (json as { geonames?: unknown } | null)?.geonames
  if (!Array.isArray(hits)) return []
  const round = (n: number) => Math.round(n * 100) / 100
  const places = hits.flatMap((r) => {
    const x = (r ?? {}) as GeoNamesHit
    const lat = Number(x.lat)
    const lng = Number(x.lng)
    if (!x.name || !x.countryCode || x.lat === undefined || x.lng === undefined || !Number.isFinite(lat) || !Number.isFinite(lng)) return []
    const p = GeoPlace.safeParse({
      name: x.name,
      countryCode: x.countryCode.toUpperCase(),
      country: x.countryName || x.countryCode,
      ...(x.adminName1 ? { admin1: x.adminName1 } : {}),
      at: [round(lat), round(lng)],
      ...(x.timezone?.timeZoneId ? { timezone: x.timezone.timeZoneId } : {}),
      // GeoNames says 0 when it doesn't know.
      ...(typeof x.population === 'number' && x.population > 0 ? { population: x.population } : {}),
    })
    return p.success ? [{ place: p.data, town: x.fcl === 'P' || x.fcl === 'A' }] : []
  })
  return [...places.filter((p) => p.town), ...places.filter((p) => !p.town)].map((p) => p.place)
}

/**
 * The island to suggest for a stage: a built place named like it (Kyoto → kyoto); in Japan, the
 * generic city / town / village by size (unknown size → Castle Town); elsewhere the placeholder.
 */
export function suggestIsland(place: { name: string; countryCode?: string; population?: number }, named: readonly { id: string; title: string }[]): string {
  const want = place.name.trim().toLowerCase()
  const hit = named.find((p) => p.title.toLowerCase() === want || p.id === want)
  if (hit) return hit.id
  if (place.countryCode && place.countryCode !== 'JP') return GENERIC_SPOT
  const pop = place.population
  if (pop !== undefined && pop >= 300_000) return JAPAN_GENERIC.city
  if (pop !== undefined && pop < 15_000) return JAPAN_GENERIC.village
  return JAPAN_GENERIC.town
}

export interface IslandChoice {
  id: string
  title: string
  group: 'japan-generic' | 'named' | 'generic'
}

/**
 * The stage editor's island picker: "Any Japanese city / town / village" first, then the named
 * places, then (only for a stage outside Japan) the generic placeholder.
 */
export function islandChoices(named: readonly { id: string; title: string }[], countryCode?: string): IslandChoice[] {
  const generic = new Set<string>(Object.values(JAPAN_GENERIC))
  const out: IslandChoice[] = [
    { id: JAPAN_GENERIC.city, title: 'Any Japanese city (Big City)', group: 'japan-generic' },
    { id: JAPAN_GENERIC.town, title: 'Any Japanese town (Castle Town)', group: 'japan-generic' },
    { id: JAPAN_GENERIC.village, title: 'Any Japanese village (Mountain Village)', group: 'japan-generic' },
    ...named.filter((p) => !generic.has(p.id)).map((p) => ({ id: p.id, title: p.title, group: 'named' as const })),
  ]
  if (countryCode && countryCode !== 'JP') out.push({ id: GENERIC_SPOT, title: genericStandIn('') ? 'Any town (outside Japan)' : 'Generic town (placeholder)', group: 'generic' })
  return out
}
